import { clearOtherAdvancedStyles } from "@blocks/utils";
import { Tab, TabList, TabPanel, TabPanels, Tabs } from "@chakra-ui/react";
import { __ } from "@wordpress/i18n";
import React, { useEffect, useMemo } from "react";
import AdvancedControls from "../../../components/common/AdvancedControls";
import InspectorTabs, {
	AdvancedInspectorControls,
	GeneralInspectorControls,
	StyleInspectorControls,
} from "../../../components/common/InspectorTabs";
import LayoutOptionsRenderer from "../../../components/common/LayoutOptionsRenderer";
import Panel from "../../../components/common/Panel";
import ResponsiveControls from "../../../components/common/ResponsiveControls";
import ZStack from "../../../components/common/ZStack";
import {
	AlignCenter,
	AlignLeft,
	AlignRight,
	AlignSpaceBetween,
	GridModuleLayout1Style1,
	GridModuleLayout1Style2,
	GridModuleLayout2Style1,
	HeadingLayout1Style1,
	HeadingLayout2Style1,
	HeadingLayout3Style1,
	HeadingLayout4Style1,
	HeadingLayout5Style1,
	HeadingLayout6Style1,
	HeadingLayout7Style1,
	HeadingLayout8Style1,
	HeadingLayout9Style1,
	LayoutAlignBottom,
	LayoutAlignLeft,
	LayoutAlignMiddle,
	LayoutAlignRight,
	LayoutAlignTop,
	TextAlignCenter,
	TextAlignLeft,
	TextAlignRight,
} from "../../../components/icons/Icons";
import {
	Background,
	Border,
	BoxShadow,
	ColorPicker,
	Dimensions,
	IconPicker,
	Input,
	PopoverDrawer,
	Select,
	Slider,
	Toggle,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
	UrlInput,
} from "../../../controls";
import Reset from "../../../controls/common/Reset";

type Props = {
	attributes: any;
	setAttributes: (attributes: any) => void;
	categories?: any[];
	tags?: any[];
	authorOptions?: any[];
	postTypeOptions?: any[];
};

const InspectorControls: React.ComponentType<Props> = (props) => {
	const {
		attributes: {
			layout,
			layout1AdvancedStyle,
			layout2AdvancedStyle,
			gap,
			category,
			postType,

			tag,
			orderBy,
			orderType,
			authorName,
			excludedCategory,
			column,
			blockPadding,
			blockMargin,
			label,
			postCount,
			offset,

			enableHeading,
			headingLayout,
			headingLayout1AdvancedStyle,
			headingLayout2AdvancedStyle,
			headingLayout3AdvancedStyle,
			headingLayout4AdvancedStyle,
			headingLayout5AdvancedStyle,
			headingLayout6AdvancedStyle,
			headingLayout7AdvancedStyle,
			headingLayout8AdvancedStyle,
			headingLayout9AdvancedStyle,
			headingColor,
			headingHoverColor,
			headingBorder,
			headingBorderBefore,
			headingBorderBottomInset,
			headingPadding,
			headingTypography,
			headingBackground,

			dotColor,
			lineAccent,
			lineColor,

			enableCategory,
			enableComment,
			categoryPosition,
			categoryColor,
			categoryBackground,
			categoryPadding,
			categoryTypography,
			enableCategoryBorder,
			categoryBorder,
			categoryMargin,
			enableMetaSeparator,
			separatorType,
			enableIcon,

			postTitleMarkup,
			enablePostTitle,
			postTitleTypography,
			postTitleColor,
			postTitleHoverColor,

			featuredImageHeight,
			featuredImageBorderRadius,
			hoverAnimation,

			metaPosition,
			metaIconColor,
			metaLinkColor,
			metaLinkHoverColor,
			metaTypography,
			metaSeparatorColor,
			metaPadding,
			metaJustification,
			categoryJustification,

			enableAuthor,
			enableDate,
			enableReadTime,
			enableViewCount,

			excerptLimit,
			enableExcerpt,
			excerptColor,
			excerptMargin,
			excerptTypography,

			enableReadMore,
			readMoreText,
			readMoreColor,
			readMorewHoverColor,
			readMoreBackground,
			readMoreHoverBackground,
			readMoreSpacing,
			iconSize,
			readMorePadding,
			readMoreBorder,
			readMoreHoverBorder,
			readMoreBoxShadow,
			readMoreBoxShadowHover,

			enableViewMore,
			viewMoreText,
			viewButtonPosition,
			enableViewMoreIcon,
			viewMoreIcon,
			viewMoreColor,
			viewMoreHoverColor,
			viewMoreBackground,
			viewMoreHoverBackground,
			viewMoreSpacing,
			viewMorePadding,
			enableViewMoreBorder,
			viewMoreBorder,
			viewMoreHoverBorder,
			viewMoreBoxShadow,
			viewMoreBoxShadowHover,
			viewMoreLink,
			viewAllTypography,

			enablePagination,
			paginationAlignment,
			paginationColor,
			paginationBackground,
			paginationHoverColor,
			paginationHoverBackground,
			paginationPadding,
			enableReadMoreBorder,

			paginationBorder,
			paginationBoxShadow,
			paginationHoverBorder,
			paginationBoxShadowHover,

			enableNumberList,
			numberLayout,
			numberPosition,
			numberListTypography,
			numberColor,
			numberLeftGap,
			numberTopGap,
			numberBackground,
			numberBorder,
			numberBoxShadow,
			numberWidth,
			numberHeight,

			alignment,
			cardPadding,
			contentPadding,
			border,
			borderHover,

			postBoxBackground,
			postBoxHoverBackground,
			enablePostBoxBorder,
			postBoxBorder,
			postBoxHoverBorder,
			enablePostTitleBorder,
			postTitleBorder,
			postTitleHoverBorder,
			postTitlePadding,

			hideOnDesktop,
			hideOnTablet,
			hideOnMobile,

			gridBackground,
			gridBoxShadow,
		},
		setAttributes,
		categories,
		tags,
		authorOptions,
		postTypeOptions,
	} = props;

	useEffect(() => {
		const type = wp.data.select("core/editor").getCurrentPost().type;

		if (type === "mzb-builder-template") {
			setAttributes({ postType: "mzb-builder" });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const gridLayoutsData = useMemo(
		() => [
			{
				id: "layout-1",
				title: __("Layout 1", "magazine-blocks"),
				attrKey: "layout1AdvancedStyle",
				value: layout1AdvancedStyle,
				options: [
					{
						label: __("Style 1", "magazine-blocks"),
						value: "layout-1-style-1",
						icon: GridModuleLayout1Style1,
					},
					{
						label: __("Style 2", "magazine-blocks"),
						value: "layout-1-style-2",
						icon: GridModuleLayout1Style2,
					},
				],
			},
			{
				id: "layout-2",
				title: __("Layout 2", "magazine-blocks"),
				attrKey: "layout2AdvancedStyle",
				value: layout2AdvancedStyle,
				options: [
					{
						label: __("Style 1", "magazine-blocks"),
						value: "layout-2-style-1",
						icon: GridModuleLayout2Style1,
					},
				],
			},
		],
		[layout1AdvancedStyle, layout2AdvancedStyle]
	);

	const headingLayoutsData = useMemo(
		() => [
			{
				id: "heading-layout-1",
				title: __("Layout 1", "magazine-blocks"),
				attrKey: "headingLayout1AdvancedStyle",
				value: headingLayout1AdvancedStyle,
				options: [
					{
						label: __("Style 1", "magazine-blocks"),
						value: "heading-layout-1-style-1",
						icon: HeadingLayout1Style1,
					},
				],
			},
			{
				id: "heading-layout-2",
				title: __("Layout 2", "magazine-blocks"),
				attrKey: "headingLayout2AdvancedStyle",
				value: headingLayout2AdvancedStyle,
				options: [
					{
						label: __("Style 1", "magazine-blocks"),
						value: "heading-layout-2-style-1",
						icon: HeadingLayout2Style1,
					},
				],
			},
			{
				id: "heading-layout-3",
				title: __("Layout 3", "magazine-blocks"),
				attrKey: "headingLayout3AdvancedStyle",
				value: headingLayout3AdvancedStyle,
				options: [
					{
						label: __("Style 1", "magazine-blocks"),
						value: "heading-layout-3-style-1",
						icon: HeadingLayout3Style1,
					},
				],
			},
			{
				id: "heading-layout-4",
				title: __("Layout 4", "magazine-blocks"),
				attrKey: "headingLayout4AdvancedStyle",
				value: headingLayout4AdvancedStyle,
				options: [
					{
						label: __("Style 1", "magazine-blocks"),
						value: "heading-layout-4-style-1",
						icon: HeadingLayout4Style1,
					},
				],
			},
			{
				id: "heading-layout-5",
				title: __("Layout 5", "magazine-blocks"),
				attrKey: "headingLayout5AdvancedStyle",
				value: headingLayout5AdvancedStyle,
				options: [
					{
						label: __("Style 1", "magazine-blocks"),
						value: "heading-layout-5-style-1",
						icon: HeadingLayout5Style1,
					},
				],
			},
			{
				id: "heading-layout-6",
				title: __("Layout 6", "magazine-blocks"),
				attrKey: "headingLayout6AdvancedStyle",
				value: headingLayout6AdvancedStyle,
				options: [
					{
						label: __("Style 1", "magazine-blocks"),
						value: "heading-layout-6-style-1",
						icon: HeadingLayout6Style1,
					},
				],
			},
			{
				id: "heading-layout-7",
				title: __("Layout 7", "magazine-blocks"),
				attrKey: "headingLayout7AdvancedStyle",
				value: headingLayout7AdvancedStyle,
				options: [
					{
						label: __("Style 1", "magazine-blocks"),
						value: "heading-layout-7-style-1",
						icon: HeadingLayout7Style1,
					},
				],
			},
			{
				id: "heading-layout-8",
				title: __("Layout 8", "magazine-blocks"),
				attrKey: "headingLayout8AdvancedStyle",
				value: headingLayout8AdvancedStyle,
				options: [
					{
						label: __("Style 1", "magazine-blocks"),
						value: "heading-layout-8-style-1",
						icon: HeadingLayout8Style1,
					},
				],
			},
			{
				id: "heading-layout-9",
				title: __("Layout 9", "magazine-blocks"),
				attrKey: "headingLayout9AdvancedStyle",
				value: headingLayout9AdvancedStyle,
				options: [
					{
						label: __("Style 1", "magazine-blocks"),
						value: "heading-layout-9-style-1",
						icon: HeadingLayout9Style1,
					},
				],
			},
		],
		[
			headingLayout1AdvancedStyle,
			headingLayout2AdvancedStyle,
			headingLayout3AdvancedStyle,
			headingLayout4AdvancedStyle,
			headingLayout5AdvancedStyle,
			headingLayout6AdvancedStyle,
			headingLayout7AdvancedStyle,
			headingLayout8AdvancedStyle,
			headingLayout9AdvancedStyle,
		]
	);

	return (
		<>
			<InspectorTabs />
			<GeneralInspectorControls>
				<Panel title={__("Layout", "magazine-blocks")} initialOpen>
					<LayoutOptionsRenderer
						layoutsData={gridLayoutsData}
						selectedValues={Object.fromEntries(
							gridLayoutsData.map((ld) => [ld.attrKey, ld.value])
						)}
						onChange={(attrKey, value, layoutId) =>
							setAttributes({
								...clearOtherAdvancedStyles(
									gridLayoutsData,
									attrKey
								),
								[attrKey]: value,
								layout: layoutId,
							})
						}
						currentLayout={layout}
					/>
					<Select
						value={column}
						onChange={(val) => setAttributes({ column: val })}
						label={__("Column", "magazine-blocks")}
						options={[
							{
								label: __("2", "magazine-blocks"),
								value: "2",
							},
							{
								label: __("3", "magazine-blocks"),
								value: "3",
							},
							{
								label: __("4", "magazine-blocks"),
								value: "4",
							},
						]}
					/>
					<Slider
						onChange={(val: any) => setAttributes({ gap: val })}
						label={__("Gap", "magazine-blocks")}
						units={["px", "em", "%"]}
						responsive={true}
						min={0}
						max={100}
						value={gap}
						resetAttributeKey="gap"
					/>
					<ToggleButtonGroup
						value={alignment}
						onChange={(v) => {
							setAttributes({ alignment: v });
						}}
						label={__("Alignment", "magazine-blocks")}
						responsive
						groupProps={{
							sx: {
								".chakra-button": {
									minH: "32px",
									svg: {
										w: 6,
										h: 6,
									},
								},
							},
						}}
					>
						<ToggleButton value="left">
							<TextAlignLeft />
						</ToggleButton>
						<ToggleButton value="center">
							<TextAlignCenter />
						</ToggleButton>
						<ToggleButton value="right">
							<TextAlignRight />
						</ToggleButton>
					</ToggleButtonGroup>
				</Panel>
				<Panel title={__("Post Options", "magazine-blocks")}>
					<Select
						options={postTypeOptions ?? []}
						onChange={(val) => setAttributes({ postType: val })}
						value={postType}
						label={__("Post Type", "magazine-blocks")}
					/>
					<Slider
						label={__("Post Count", "magazine-blocks")}
						value={postCount}
						step={1}
						min={2}
						max={15}
						onChange={(val) => setAttributes({ postCount: val })}
						resetAttributeKey="postCount"
					/>
					<Slider
						label={__("Offset", "magazine-blocks")}
						value={offset}
						step={1}
						min={0}
						max={15}
						onChange={(val) => setAttributes({ offset: val })}
						resetAttributeKey="offset"
					/>
					<Select
						options={[
							{
								label: __("Date", "magazine-blocks"),
								value: "date",
							},
							{
								label: __("Title", "magazine-blocks"),
								value: "title",
							},
							{
								label: __("Random", "magazine-blocks"),
								value: "rand",
							},
						]}
						onChange={(val) => setAttributes({ orderBy: val })}
						value={orderBy}
						label={__("Order By", "magazine-blocks")}
					/>
					<>
						<ToggleButtonGroup
							value={orderType}
							onChange={(v) => {
								setAttributes({ orderType: v });
							}}
							label={__("Order", "magazine-blocks")}
							groupProps={{
								sx: {
									".chakra-button": {
										minH: "32px",
										color: "#1A202C",
										svg: {
											w: 6,
											h: 6,
										},
										"&[data-active]": {
											color: "#fff !important",
										},
									},
								},
							}}
						>
							<ToggleButton value="asc">Ascending</ToggleButton>
							<ToggleButton value="desc">Descending</ToggleButton>
						</ToggleButtonGroup>
					</>
					{postType !== "mzb-builder" && (
						<>
							<Select
								value={category}
								onChange={(val) =>
									setAttributes({ category: val })
								}
								label={__("Category", "magazine-blocks")}
								options={[
									{ value: "all", label: __("All") },
									...(categories ?? [])?.map(
										({ name, id }) => ({
											label: name,
											value: id.toString(),
										})
									),
								]}
							/>

							<Select
								isMulti
								value={excludedCategory}
								onChange={(val) =>
									setAttributes({ excludedCategory: val })
								}
								label={__(
									"Excluded Category",
									"magazine-blocks"
								)}
								options={[
									...(categories ?? [])?.map(
										({ name, id }) => ({
											label: name,
											value: id.toString(),
										})
									),
								]}
							/>
							<Select
								value={tag}
								onChange={(val) => setAttributes({ tag: val })}
								label={__("Tag", "magazine-blocks")}
								options={[
									{ value: "all", label: __("All") },
									...(tags ?? [])?.map(({ name, id }) => ({
										label: name,
										value: id.toString(),
									})),
								]}
							/>
							<Select
								options={[
									{
										value: "all",
										label: __("All", "magazine-blocks"),
									},
									...(authorOptions ?? []),
								]}
								onChange={(val) =>
									setAttributes({ authorName: val })
								}
								value={authorName}
								label={__("Authors", "magazine-blocks")}
							/>
						</>
					)}
				</Panel>
				<Panel title={__("Heading", "magazine-blocks")}>
					<Toggle
						checked={enableHeading}
						onChange={() =>
							setAttributes({ enableHeading: !enableHeading })
						}
						label={__("Enable", "magazine-blocks")}
					/>
					{enableHeading && (
						<>
							<Input
								onChange={(val) =>
									setAttributes({ label: val })
								}
								label={__("Label", "magazine-blocks")}
								value={label || ""}
							/>
							<LayoutOptionsRenderer
								columns={2}
								layoutsData={headingLayoutsData}
								selectedValues={Object.fromEntries(
									headingLayoutsData.map((ld) => [
										ld.attrKey,
										ld.value,
									])
								)}
								onChange={(attrKey, value, layoutId) =>
									setAttributes({
										...clearOtherAdvancedStyles(
											headingLayoutsData,
											attrKey
										),
										[attrKey]: value,
										headingLayout: layoutId,
									})
								}
								currentLayout={headingLayout}
							/>
						</>
					)}
				</Panel>
				<Panel title={__("Header Meta", "magazine-blocks")}>
					<Toggle
						checked={enableCategory}
						onChange={() =>
							setAttributes({ enableCategory: !enableCategory })
						}
						label={__("Category", "magazine-blocks")}
					/>
					<Toggle
						checked={enableComment}
						onChange={() =>
							setAttributes({ enableComment: !enableComment })
						}
						label={__("Comment", "magazine-blocks")}
					/>
					<ToggleButtonGroup
						value={categoryJustification}
						onChange={(v) => {
							setAttributes({ categoryJustification: v });
						}}
						label={__("Justification", "magazine-blocks")}
						responsive
						groupProps={{
							sx: {
								".chakra-button": {
									minH: "32px",
									"&[data-active]": {
										svg: {
											fill: "#fff !important",
										},
									},
								},
							},
						}}
					>
						<ToggleButton value="left">
							<AlignLeft />
						</ToggleButton>
						<ToggleButton value="center">
							<AlignCenter />
						</ToggleButton>
						<ToggleButton value="right">
							<AlignRight />
						</ToggleButton>
						<ToggleButton value="space-between">
							<AlignSpaceBetween />
						</ToggleButton>
					</ToggleButtonGroup>
					{"layout-2" === layout && (
						<Select
							value={categoryPosition}
							onChange={(val) =>
								setAttributes({ categoryPosition: val })
							}
							label={__("Position", "magazine-blocks")}
							options={[
								{
									label: __(
										"Outside Image",
										"magazine-blocks"
									),
									value: "out-image",
								},
								{
									label: __(
										"Inside Image",
										"magazine-blocks"
									),
									value: "in-image",
								},
							]}
						/>
					)}
				</Panel>
				<Panel title={__("Post Title", "magazine-blocks")}>
					<Toggle
						checked={enablePostTitle}
						onChange={() =>
							setAttributes({ enablePostTitle: !enablePostTitle })
						}
						label={__("Enable", "magazine-blocks")}
					/>

					{enablePostTitle && (
						<Select
							options={[
								{
									label: __("H1", "magazine-blocks"),
									value: "h1",
								},
								{
									label: __("H2", "magazine-blocks"),
									value: "h2",
								},
								{
									label: __("H3", "magazine-blocks"),
									value: "h3",
								},
								{
									label: __("H4", "magazine-blocks"),
									value: "h4",
								},
								{
									label: __("H5", "magazine-blocks"),
									value: "h5",
								},
								{
									label: __("H6", "magazine-blocks"),
									value: "h6",
								},
							]}
							onChange={(val) =>
								setAttributes({ postTitleMarkup: val })
							}
							value={postTitleMarkup}
							label={__("HTML Markup", "magazine-blocks")}
							// @ts-ignore
							inline={false}
						/>
					)}
				</Panel>
				<Panel title={__("Image", "magazine-blocks")}>
					<Select
						options={[
							{
								label: __("None", "magazine-blocks"),
								value: "none",
							},
							{
								label: __("Zoom In", "magazine-blocks"),
								value: "zoomIn",
							},
							{
								label: __("Zoom Out", "magazine-blocks"),
								value: "zoomOut",
							},
							{
								label: __("Opacity", "magazine-blocks"),
								value: "opacity",
							},
							{
								label: __("Rotate Left", "magazine-blocks"),
								value: "rotateLeft",
							},
							{
								label: __("Rotate Right", "magazine-blocks"),
								value: "rotateRight",
							},
							{
								label: __("Slide Left", "magazine-blocks"),
								value: "slideLeft",
							},
							{
								label: __("Slide Right", "magazine-blocks"),
								value: "slideRight",
							},
						]}
						onChange={(val) =>
							setAttributes({ hoverAnimation: val })
						}
						value={hoverAnimation}
						label={__("Hover Animation", "magazine-blocks")}
					/>
				</Panel>
				<Panel title={__("Meta", "magazine-blocks")}>
					{(enableDate ||
						enableAuthor ||
						enableReadTime ||
						enableViewCount) && (
						<>
							<ToggleButtonGroup
								value={metaPosition}
								onChange={(v) => {
									setAttributes({ metaPosition: v });
								}}
								label={__("Position", "magazine-blocks")}
								groupProps={{
									sx: {
										".chakra-button": {
											minH: "32px",
											svg: {
												w: 6,
												h: 6,
											},
										},
									},
								}}
							>
								<ToggleButton value="top">
									<LayoutAlignTop />
								</ToggleButton>
								<ToggleButton value="bottom">
									<LayoutAlignBottom />
								</ToggleButton>
							</ToggleButtonGroup>
							<ToggleButtonGroup
								value={metaJustification}
								onChange={(v) => {
									setAttributes({ metaJustification: v });
								}}
								label={__("Justification", "magazine-blocks")}
								responsive
								groupProps={{
									sx: {
										".chakra-button": {
											minH: "32px",
											"&[data-active]": {
												svg: {
													fill: "#fff !important",
												},
											},
										},
									},
								}}
							>
								<ToggleButton value="left">
									<AlignLeft />
								</ToggleButton>
								<ToggleButton value="center">
									<AlignCenter />
								</ToggleButton>
								<ToggleButton value="right">
									<AlignRight />
								</ToggleButton>
								<ToggleButton value="space-between">
									<AlignSpaceBetween />
								</ToggleButton>
							</ToggleButtonGroup>
						</>
					)}
					<Toggle
						checked={enableAuthor}
						onChange={() =>
							setAttributes({ enableAuthor: !enableAuthor })
						}
						label={__("Author", "magazine-blocks")}
					/>
					<Toggle
						checked={enableDate}
						onChange={() =>
							setAttributes({ enableDate: !enableDate })
						}
						label={__("Date", "magazine-blocks")}
					/>
					<Toggle
						checked={enableReadTime}
						onChange={() =>
							setAttributes({ enableReadTime: !enableReadTime })
						}
						label={__("Read Time", "magazine-blocks")}
					/>
					<Toggle
						checked={enableViewCount}
						onChange={() =>
							setAttributes({ enableViewCount: !enableViewCount })
						}
						label={__("View Count", "magazine-blocks")}
					/>
					<Toggle
						checked={enableMetaSeparator}
						onChange={() =>
							setAttributes({
								enableMetaSeparator: !enableMetaSeparator,
							})
						}
						label={__("Separator", "magazine-blocks")}
					/>
					{enableMetaSeparator && (
						<Select
							options={[
								{
									label: __("None", "magazine-blocks"),
									value: "none",
								},
								{
									label: __("Circle", "magazine-blocks"),
									value: "circle",
								},
								{
									label: __("Square", "magazine-blocks"),
									value: "square",
								},
								{
									label: __(
										"Vertical Dash",
										"magazine-blocks"
									),
									value: "vertical-dash",
								},
								{
									label: __(
										"Horizontal Dash",
										"magazine-blocks"
									),
									value: "horizontal-dash",
								},
								{
									label: __(
										"Outline Circle",
										"magazine-blocks"
									),
									value: "outline-circle",
								},
							]}
							onChange={(val) =>
								setAttributes({ separatorType: val })
							}
							value={separatorType}
							label={__("Separator Type", "magazine-blocks")}
						/>
					)}
					<Toggle
						checked={enableIcon}
						onChange={() =>
							setAttributes({ enableIcon: !enableIcon })
						}
						label={__("Icon", "magazine-blocks")}
					/>
				</Panel>
				<Panel title={__("Excerpt", "magazine-blocks")}>
					<Toggle
						checked={enableExcerpt}
						onChange={() =>
							setAttributes({ enableExcerpt: !enableExcerpt })
						}
						label={__("Enable", "magazine-blocks")}
					/>
					{enableExcerpt && (
						<Slider
							onChange={(val) =>
								setAttributes({ excerptLimit: val })
							}
							label={__("Limit", "magazine-blocks")}
							min={0}
							max={500}
							step={1}
							value={excerptLimit}
							resetAttributeKey="excerptLimit"
						/>
					)}
				</Panel>
				<Panel title={__("Read More", "magazine-blocks")}>
					<Toggle
						checked={enableReadMore}
						onChange={() =>
							setAttributes({
								enableReadMore: !enableReadMore,
							})
						}
						label={__("Enable", "magazine-blocks")}
					/>
					{enableReadMore && (
						<Input
							value={readMoreText}
							label={__("Text", "magazine-blocks")}
							onChange={(val) =>
								setAttributes({ readMoreText: val })
							}
						/>
					)}
				</Panel>
				<Panel title={__("View All", "magazine-blocks")}>
					<Toggle
						checked={enableViewMore}
						onChange={() =>
							setAttributes({
								enableViewMore: !enableViewMore,
							})
						}
						label={__("Enable", "magazine-blocks")}
					/>
					{enableViewMore && (
						<>
							<Input
								value={viewMoreText}
								label={__("Text", "magazine-blocks")}
								onChange={(val) =>
									setAttributes({ viewMoreText: val })
								}
							/>
							<Select
								options={[
									{
										label: __("Top", "magazine-blocks"),
										value: "top",
									},
									{
										label: __("Bottom", "magazine-blocks"),
										value: "bottom",
									},
								]}
								onChange={(val) =>
									setAttributes({ viewButtonPosition: val })
								}
								value={viewButtonPosition}
								label={__(
									"View Button Position",
									"magazine-blocks"
								)}
							/>
							<Toggle
								checked={enableViewMoreIcon}
								onChange={() =>
									setAttributes({
										enableViewMoreIcon: !enableViewMoreIcon,
									})
								}
								label={__("Enable Icon", "magazine-blocks")}
							/>
							{enableViewMoreIcon && (
								<IconPicker
									value={viewMoreIcon}
									onChange={(val) =>
										setAttributes({ viewMoreIcon: val })
									}
								/>
							)}
							<UrlInput
								onChange={(val) =>
									setAttributes({ viewMoreLink: val })
								}
								label={__("View More Link", "magazine-blocks")}
								value={viewMoreLink}
								newTab
								noFollow
							/>
						</>
					)}
				</Panel>
				<Panel title={__("Pagination", "magazine-blocks")}>
					<Toggle
						checked={enablePagination}
						onChange={() =>
							setAttributes({
								enablePagination: !enablePagination,
							})
						}
						label={__("Enable", "magazine-blocks")}
					/>
					{enablePagination && (
						<ToggleButtonGroup
							value={paginationAlignment}
							onChange={(v) => {
								setAttributes({ paginationAlignment: v });
							}}
							label={__("Alignment", "magazine-blocks")}
							responsive
							groupProps={{
								sx: {
									".chakra-button": {
										minH: "32px",
										svg: {
											w: 6,
											h: 6,
										},
									},
								},
							}}
						>
							<ToggleButton value="left">
								<LayoutAlignLeft />
							</ToggleButton>
							<ToggleButton value="center">
								<LayoutAlignMiddle />
							</ToggleButton>
							<ToggleButton value="right">
								<LayoutAlignRight />
							</ToggleButton>
						</ToggleButtonGroup>
					)}
				</Panel>
				<Panel title={__("Number List", "magazine-blocks")}>
					<Toggle
						checked={enableNumberList}
						onChange={() =>
							setAttributes({
								enableNumberList: !enableNumberList,
							})
						}
						label={__("Enable", "magazine-blocks")}
					/>
					{enableNumberList && (
						<>
							<Select
								value={numberLayout}
								onChange={(val) =>
									setAttributes({ numberLayout: val })
								}
								label={__("Number Layout", "magazine-blocks")}
								options={[
									{
										label: __("Default", "magazine-blocks"),
										value: "default",
									},
									{
										label: __("Zero", "magazine-blocks"),
										value: "zero",
									},
									{
										label: __("Circle", "magazine-blocks"),
										value: "circle",
									},
									{
										label: __("Square", "magazine-blocks"),
										value: "square",
									},
									{
										label: __("Dot", "magazine-blocks"),
										value: "dot",
									},
								]}
							/>
							<Select
								value={numberPosition}
								onChange={(val) =>
									setAttributes({ numberPosition: val })
								}
								label={__("Number Layout", "magazine-blocks")}
								options={[
									{
										label: __(
											"With Image",
											"magazine-blocks"
										),
										value: "with-image",
									},
									{
										label: __(
											"With Text",
											"magazine-blocks"
										),
										value: "with-text",
									},
								]}
							/>
						</>
					)}
				</Panel>
			</GeneralInspectorControls>
			<StyleInspectorControls>
				<Panel title={__("General", "magazine-blocks")} initialOpen>
					<PopoverDrawer label={__("Background", "magazine-blocks")}>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={gridBackground}
										resetKey="gridBackground"
										onReset={(v) => {
											setAttributes({
												gridBackground: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<Background
										value={gridBackground}
										onChange={(v) =>
											setAttributes({
												gridBackground: v,
											})
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
					<BoxShadow
						resetAttributeKey="gridBoxShadow"
						value={gridBoxShadow}
						onChange={(val) =>
							setAttributes({
								gridBoxShadow: val,
							})
						}
					/>
					{layout === "layout-2" &&
						layout2AdvancedStyle === "layout-2-style-1" && (
							<Dimensions
								value={cardPadding}
								responsive
								label={__("Card Padding", "magazine-blocks")}
								type="padding"
								defaultUnit="px"
								units={["px", "rem", "em", "%"]}
								onChange={(val) =>
									setAttributes({ cardPadding: val })
								}
								resetAttributeKey="cardPadding"
							/>
						)}
					<Dimensions
						value={contentPadding}
						responsive
						label={__("Content Padding", "magazine-blocks")}
						type="padding"
						defaultUnit="px"
						units={["px", "rem", "em", "%"]}
						onChange={(val) =>
							setAttributes({ contentPadding: val })
						}
						resetAttributeKey="contentPadding"
					/>
				</Panel>
				{enableHeading && (
					<Panel title={__("Heading", "magazine-blocks")}>
						<PopoverDrawer
							label={__("Heading Color", "magazine-blocks")}
							trigger={(props) => (
								<PopoverDrawer.Trigger {...props}>
									<ZStack
										items={[
											{
												color: headingColor,
												id: "categoryTitleColor",
											},
											{
												color: headingHoverColor,
												id: "categoryTitleHoverColor",
											},
										]}
									/>
								</PopoverDrawer.Trigger>
							)}
							closeOnFocusOutside
						>
							<Tabs position="relative">
								<TabList>
									<Tab>{__("Normal", "magazine-blocks")}</Tab>
									<Tab>{__("Hover", "magazine-blocks")}</Tab>
								</TabList>
								<TabPanels>
									<TabPanel>
										<Reset
											saved={headingColor}
											resetKey="headingColor"
											onReset={(v) => {
												setAttributes({
													headingColor: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={headingColor}
											onChange={(v) =>
												setAttributes({
													headingColor: v,
												})
											}
										/>
									</TabPanel>
									<TabPanel>
										<Reset
											saved={headingHoverColor}
											resetKey="headingHoverColor"
											onReset={(v) => {
												setAttributes({
													headingHoverColor: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={headingHoverColor}
											onChange={(v) =>
												setAttributes({
													headingHoverColor: v,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						</PopoverDrawer>
						{("heading-layout-6" === headingLayout ||
							"heading-layout-7" === headingLayout) && (
							<>
								<Dimensions
									value={headingPadding}
									responsive
									label={__("Padding", "magazine-blocks")}
									type="padding"
									defaultUnit="px"
									units={["px", "rem", "em", "%"]}
									onChange={(val) =>
										setAttributes({
											headingPadding: val,
										})
									}
									resetAttributeKey="headingPadding"
								/>
							</>
						)}
						<Typography
							value={headingTypography}
							onChange={(val) =>
								setAttributes({ headingTypography: val })
							}
							resetAttributeKey="headingTypography"
						/>
						{"heading-layout-2" === headingLayout && (
							<PopoverDrawer
								label={__("Background", "magazine-blocks")}
							>
								<Tabs position="relative">
									<TabList>
										<Tab>
											{__("Normal", "magazine-blocks")}
										</Tab>
									</TabList>
									<TabPanels>
										<TabPanel>
											<Reset
												saved={headingBackground}
												resetKey="headingBackground"
												onReset={(v) => {
													setAttributes({
														headingBackground: v,
													});
												}}
												buttonProps={{
													position: "absolute",
													top: "12px",
													right: "10px",
												}}
											/>
											<Background
												value={headingBackground}
												onChange={(v) =>
													setAttributes({
														headingBackground: v,
													})
												}
											/>
										</TabPanel>
									</TabPanels>
								</Tabs>
							</PopoverDrawer>
						)}

						{("heading-layout-1" === headingLayout ||
							"heading-layout-3" === headingLayout ||
							"heading-layout-7" === headingLayout ||
							"heading-layout-8" === headingLayout) && (
							<>
								<PopoverDrawer
									label={__("Line Accent", "magazine-blocks")}
									trigger={(props) => (
										<PopoverDrawer.Trigger {...props}>
											<ZStack
												items={[
													{
														color: lineAccent,
														id: "lineAccent",
													},
												]}
											/>
										</PopoverDrawer.Trigger>
									)}
									closeOnFocusOutside
								>
									<Tabs position="relative">
										<TabList>
											<Tab>
												{__(
													"Normal",
													"magazine-blocks"
												)}
											</Tab>
										</TabList>
										<TabPanels>
											<TabPanel>
												<Reset
													saved={lineAccent}
													resetKey="lineAccent"
													onReset={(v) => {
														setAttributes({
															lineAccent: v,
														});
													}}
													buttonProps={{
														position: "absolute",
														top: "12px",
														right: "10px",
													}}
												/>
												<ColorPicker
													value={lineAccent}
													onChange={(v) =>
														setAttributes({
															lineAccent: v,
														})
													}
												/>
											</TabPanel>
										</TabPanels>
									</Tabs>
								</PopoverDrawer>
							</>
						)}

						{("heading-layout-2" === headingLayout ||
							"heading-layout-3" === headingLayout ||
							"heading-layout-4" === headingLayout ||
							"heading-layout-5" === headingLayout ||
							"heading-layout-5" === headingLayout ||
							"heading-layout-6" === headingLayout ||
							"heading-layout-7" === headingLayout ||
							"heading-layout-9" === headingLayout) && (
							<>
								<PopoverDrawer
									label={__("Line Color", "magazine-blocks")}
									trigger={(props) => (
										<PopoverDrawer.Trigger {...props}>
											<ZStack
												items={[
													{
														color: lineColor,
														id: "lineColor",
													},
												]}
											/>
										</PopoverDrawer.Trigger>
									)}
									closeOnFocusOutside
								>
									<Tabs position="relative">
										<TabList>
											<Tab>
												{__(
													"Normal",
													"magazine-blocks"
												)}
											</Tab>
										</TabList>
										<TabPanels>
											<TabPanel>
												<Reset
													saved={lineColor}
													resetKey="lineColor"
													onReset={(v) => {
														setAttributes({
															lineColor: v,
														});
													}}
													buttonProps={{
														position: "absolute",
														top: "12px",
														right: "10px",
													}}
												/>
												<ColorPicker
													value={lineColor}
													onChange={(v) =>
														setAttributes({
															lineColor: v,
														})
													}
												/>
											</TabPanel>
										</TabPanels>
									</Tabs>
								</PopoverDrawer>
							</>
						)}

						{("heading-layout-4" === headingLayout ||
							"heading-layout-5" === headingLayout) && (
							<PopoverDrawer
								label={__("Dot Color", "magazine-blocks")}
								trigger={(props) => (
									<PopoverDrawer.Trigger {...props}>
										<ZStack
											items={[
												{
													color: dotColor,
													id: "dotColor",
												},
											]}
										/>
									</PopoverDrawer.Trigger>
								)}
								closeOnFocusOutside
							>
								<Tabs position="relative">
									<TabList>
										<Tab>
											{__("Normal", "magazine-blocks")}
										</Tab>
									</TabList>
									<TabPanels>
										<TabPanel>
											<Reset
												saved={dotColor}
												resetKey="dotColor"
												onReset={(v) => {
													setAttributes({
														dotColor: v,
													});
												}}
												buttonProps={{
													position: "absolute",
													top: "12px",
													right: "10px",
												}}
											/>
											<ColorPicker
												value={dotColor}
												onChange={(v) =>
													setAttributes({
														dotColor: v,
													})
												}
											/>
										</TabPanel>
									</TabPanels>
								</Tabs>
							</PopoverDrawer>
						)}
					</Panel>
				)}
				<Panel title={__("Header Meta", "magazine-blocks")}>
					<PopoverDrawer
						label={__("Text Color", "magazine-blocks")}
						trigger={(props) => (
							<PopoverDrawer.Trigger {...props}>
								<ZStack
									items={[
										{
											color: categoryColor,
											id: "categoryColor",
										},
									]}
								/>
							</PopoverDrawer.Trigger>
						)}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={categoryColor}
										resetKey="categoryColor"
										onReset={(v) => {
											setAttributes({
												categoryColor: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={categoryColor}
										onChange={(v) =>
											setAttributes({ categoryColor: v })
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
					<PopoverDrawer label={__("Background", "magazine-blocks")}>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={categoryBackground}
										resetKey="categoryBackground"
										onReset={(v) => {
											setAttributes({
												categoryBackground: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<Background
										value={categoryBackground}
										onChange={(v) =>
											setAttributes({
												categoryBackground: v,
											})
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
					<Typography
						value={categoryTypography}
						onChange={(val) =>
							setAttributes({ categoryTypography: val })
						}
						resetAttributeKey="categoryTypography"
					/>
					<Dimensions
						value={categoryPadding}
						responsive
						label={__("Padding", "magazine-blocks")}
						type="padding"
						defaultUnit="px"
						units={["px", "rem", "em", "%"]}
						onChange={(val) =>
							setAttributes({ categoryPadding: val })
						}
						resetAttributeKey="categoryPadding"
					/>
					<Dimensions
						value={categoryMargin}
						responsive
						label={__("Margin", "magazine-blocks")}
						type="margin"
						defaultUnit="px"
						units={["px", "rem", "em", "%"]}
						onChange={(val) =>
							setAttributes({ categoryMargin: val })
						}
						resetAttributeKey="categoryMargin"
					/>
					<Toggle
						checked={enableCategoryBorder}
						onChange={() =>
							setAttributes({
								enableCategoryBorder: !enableCategoryBorder,
							})
						}
						label={__("Border", "magazine-blocks")}
					/>
					{enableCategoryBorder && (
						<Border
							value={categoryBorder}
							onChange={(v) => {
								setAttributes({
									categoryBorder: v,
								});
							}}
							resetAttributeKey="categoryBorder"
						/>
					)}
				</Panel>
				{layout === "layout-2" &&
					layout2AdvancedStyle === "layout-2-style-1" && (
						<Panel title={__("Post Box", "magazine-blocks")}>
							<PopoverDrawer
								label={__("Background", "magazine-blocks")}
							>
								<Tabs position="relative">
									<TabList>
										<Tab>
											{__("Normal", "magazine-blocks")}
										</Tab>
										<Tab>
											{__("Hover", "magazine-blocks")}
										</Tab>
									</TabList>
									<TabPanels>
										<TabPanel>
											<Reset
												saved={postBoxBackground}
												resetKey="postBoxBackground"
												onReset={(v) => {
													setAttributes({
														postBoxBackground: v,
													});
												}}
												buttonProps={{
													position: "absolute",
													top: "12px",
													right: "10px",
												}}
											/>
											<Background
												value={postBoxBackground}
												onChange={(v) =>
													setAttributes({
														postBoxBackground: v,
													})
												}
											/>
										</TabPanel>
										<TabPanel>
											<Reset
												saved={postBoxHoverBackground}
												resetKey="postBoxHoverBackground"
												onReset={(v) => {
													setAttributes({
														postBoxHoverBackground:
															v,
													});
												}}
												buttonProps={{
													position: "absolute",
													top: "12px",
													right: "10px",
												}}
											/>
											<Background
												value={postBoxHoverBackground}
												onChange={(v) =>
													setAttributes({
														postBoxHoverBackground:
															v,
													})
												}
											/>
										</TabPanel>
									</TabPanels>
								</Tabs>
							</PopoverDrawer>
							<Toggle
								checked={enablePostBoxBorder}
								onChange={() =>
									setAttributes({
										enablePostBoxBorder:
											!enablePostBoxBorder,
									})
								}
								label={__("Border", "magazine-blocks")}
							/>
							{enablePostBoxBorder && (
								<Tabs isFitted variant="unstyled">
									<TabList
										border="1px solid #E0E0E0"
										p="4px"
										borderRadius="2px"
									>
										<Tab
											_selected={{
												color: "white",
												bg: "#7E36F4",
												borderRadius: "2px",
											}}
										>
											Normal
										</Tab>
										<Tab
											_selected={{
												color: "white",
												bg: "#7E36F4",
												borderRadius: "2px",
											}}
										>
											Hover
										</Tab>
									</TabList>
									<TabPanels>
										<TabPanel p="16px 0">
											<Border
												value={postBoxBorder}
												onChange={(v) => {
													setAttributes({
														postBoxBorder: v,
													});
												}}
												resetAttributeKey="postBoxBorder"
											/>
										</TabPanel>
										<TabPanel p="16px 0">
											<Border
												value={postBoxHoverBorder}
												onChange={(v) => {
													setAttributes({
														postBoxHoverBorder: v,
													});
												}}
												resetAttributeKey="postBoxHoverBorder"
											/>
										</TabPanel>
									</TabPanels>
								</Tabs>
							)}
						</Panel>
					)}
				{enablePostTitle && (
					<Panel title={__("Post Title", "magazine-blocks")}>
						<Typography
							value={postTitleTypography}
							onChange={(val) =>
								setAttributes({ postTitleTypography: val })
							}
							resetAttributeKey="postTitleTypography"
						/>
						<PopoverDrawer
							label={__("Text Color", "magazine-blocks")}
							trigger={(props) => (
								<PopoverDrawer.Trigger {...props}>
									<ZStack
										items={[
											{
												color: postTitleColor,
												id: "postTitleColor",
											},
											{
												color: postTitleHoverColor,
												id: "postTitleHoverColor",
											},
										]}
									/>
								</PopoverDrawer.Trigger>
							)}
							closeOnFocusOutside
						>
							<Tabs position="relative">
								<TabList>
									<Tab>{__("Normal", "magazine-blocks")}</Tab>
									<Tab>{__("Hover", "magazine-blocks")}</Tab>
								</TabList>
								<TabPanels>
									<TabPanel>
										<Reset
											saved={postTitleColor}
											resetKey="postTitleColor"
											onReset={(v) => {
												setAttributes({
													postTitleColor: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={postTitleColor}
											onChange={(v) =>
												setAttributes({
													postTitleColor: v,
												})
											}
										/>
									</TabPanel>
									<TabPanel>
										<Reset
											saved={postTitleHoverColor}
											resetKey="postTitleHoverColor"
											onReset={(v) => {
												setAttributes({
													postTitleHoverColor: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={postTitleHoverColor}
											onChange={(v) =>
												setAttributes({
													postTitleHoverColor: v,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						</PopoverDrawer>
						<Dimensions
							value={postTitlePadding}
							responsive
							label={__("Padding", "magazine-blocks")}
							type="padding"
							defaultUnit="px"
							units={["px", "rem", "em", "%"]}
							onChange={(val) =>
								setAttributes({ postTitlePadding: val })
							}
							resetAttributeKey="postTitlePadding"
						/>
						<Toggle
							checked={enablePostTitleBorder}
							onChange={() =>
								setAttributes({
									enablePostTitleBorder:
										!enablePostTitleBorder,
								})
							}
							label={__("Border", "magazine-blocks")}
						/>
						{enablePostTitleBorder && (
							<Tabs isFitted variant="unstyled">
								<TabList
									border="1px solid #E0E0E0"
									p="4px"
									borderRadius="2px"
								>
									<Tab
										_selected={{
											color: "white",
											bg: "#7E36F4",
											borderRadius: "2px",
										}}
									>
										Normal
									</Tab>
									<Tab
										_selected={{
											color: "white",
											bg: "#7E36F4",
											borderRadius: "2px",
										}}
									>
										Hover
									</Tab>
								</TabList>
								<TabPanels>
									<TabPanel p="16px 0">
										<Border
											value={postTitleBorder}
											onChange={(v) => {
												setAttributes({
													postTitleBorder: v,
												});
											}}
											resetAttributeKey="postTitleBorder"
										/>
									</TabPanel>
									<TabPanel p="16px 0">
										<Border
											value={postTitleHoverBorder}
											onChange={(v) => {
												setAttributes({
													postTitleHoverBorder: v,
												});
											}}
											resetAttributeKey="postTitleHoverBorder"
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						)}
					</Panel>
				)}

				<Panel title={__("Image", "magazine-blocks")}>
					<Slider
						onChange={(val: any) =>
							setAttributes({ featuredImageHeight: val })
						}
						label={__("Height", "magazine-blocks")}
						units={["px", "em", "%"]}
						responsive={true}
						min={0}
						max={1000}
						value={featuredImageHeight}
						resetAttributeKey="featuredImageHeight"
					/>
					<Dimensions
						value={featuredImageBorderRadius || {}}
						responsive
						label={__("Radius", "magazine-blocks")}
						min={0}
						max={500}
						defaultUnit="px"
						units={["px", "rem", "em", "%"]}
						onChange={(val) =>
							setAttributes({
								featuredImageBorderRadius: val,
							})
						}
						resetAttributeKey="featuredImageBorderRadius"
					/>
				</Panel>
				<Panel title={__("Meta", "magazine-blocks")}>
					<PopoverDrawer
						label={__("Icon Color", "magazine-blocks")}
						trigger={(props) => (
							<PopoverDrawer.Trigger {...props}>
								<ZStack
									items={[
										{
											color: metaIconColor,
											id: "metaIconColor",
										},
									]}
								/>
							</PopoverDrawer.Trigger>
						)}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={metaIconColor}
										resetKey="metaIconColor"
										onReset={(v) => {
											setAttributes({ metaIconColor: v });
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={metaIconColor}
										onChange={(v) =>
											setAttributes({ metaIconColor: v })
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
					<PopoverDrawer
						label={__("Link Color", "magazine-blocks")}
						trigger={(props) => (
							<PopoverDrawer.Trigger {...props}>
								<ZStack
									items={[
										{ color: metaLinkColor, id: "color" },
										{
											color: metaLinkHoverColor,
											id: "hoverColor",
										},
									]}
								/>
							</PopoverDrawer.Trigger>
						)}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
								<Tab>{__("Hover", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={metaLinkColor}
										resetKey="metaLinkColor"
										onReset={(v) => {
											setAttributes({ metaLinkColor: v });
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={metaLinkColor}
										onChange={(v) =>
											setAttributes({ metaLinkColor: v })
										}
									/>
								</TabPanel>
								<TabPanel>
									<Reset
										saved={metaLinkHoverColor}
										resetKey="metaLinkHoverColor"
										onReset={(v) => {
											setAttributes({
												metaLinkHoverColor: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={metaLinkHoverColor}
										onChange={(v) =>
											setAttributes({
												metaLinkHoverColor: v,
											})
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
					<PopoverDrawer
						label={__("Separator Color", "magazine-blocks")}
						trigger={(props) => (
							<PopoverDrawer.Trigger {...props}>
								<ZStack
									items={[
										{
											color: metaSeparatorColor,
											id: "metaSeparatorColor",
										},
									]}
								/>
							</PopoverDrawer.Trigger>
						)}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={metaSeparatorColor}
										resetKey="metaSeparatorColor"
										onReset={(v) => {
											setAttributes({
												metaSeparatorColor: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={metaSeparatorColor}
										onChange={(v) =>
											setAttributes({
												metaSeparatorColor: v,
											})
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
					<Typography
						value={metaTypography}
						onChange={(val) =>
							setAttributes({
								metaTypography: val,
							})
						}
						resetAttributeKey="metaTypography"
					/>
					<Dimensions
						value={metaPadding}
						responsive
						label={__("Padding", "magazine-blocks")}
						type="padding"
						defaultUnit="px"
						units={["px", "rem", "em", "%"]}
						onChange={(val) => setAttributes({ metaPadding: val })}
						resetAttributeKey="metaPadding"
					/>
				</Panel>
				{enableExcerpt && (
					<Panel title={__("Excerpt", "magazine-blocks")}>
						<PopoverDrawer
							label={__("Text Color", "magazine-blocks")}
							trigger={(props) => (
								<PopoverDrawer.Trigger {...props}>
									<ZStack
										items={[
											{
												color: excerptColor,
												id: "color",
											},
										]}
									/>
								</PopoverDrawer.Trigger>
							)}
							closeOnFocusOutside
						>
							<Tabs position="relative">
								<TabList>
									<Tab>{__("Normal", "magazine-blocks")}</Tab>
								</TabList>
								<TabPanels>
									<TabPanel>
										<Reset
											saved={excerptColor}
											resetKey="excerptColor"
											onReset={(v) => {
												setAttributes({
													excerptColor: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={excerptColor}
											onChange={(v) =>
												setAttributes({
													excerptColor: v,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						</PopoverDrawer>
						<Dimensions
							value={excerptMargin}
							responsive
							label={__("Margin", "magazine-blocks")}
							type="margin"
							defaultUnit="px"
							units={["px", "rem", "em", "%"]}
							onChange={(val) =>
								setAttributes({ excerptMargin: val })
							}
							resetAttributeKey="excerptMargin"
						/>
						<Typography
							value={excerptTypography}
							onChange={(val) =>
								setAttributes({
									excerptTypography: val,
								})
							}
							resetAttributeKey="excerptTypography"
						/>
					</Panel>
				)}
				{enableReadMore && (
					<Panel title={__("Read More", "magazine-blocks")}>
						<PopoverDrawer
							label={__("Text Color", "magazine-blocks")}
							trigger={(props) => (
								<PopoverDrawer.Trigger {...props}>
									<ZStack
										items={[
											{
												color: readMoreColor,
												id: "readMoreColor",
											},
											{
												color: readMorewHoverColor,
												id: "readMorewHoverColor",
											},
										]}
									/>
								</PopoverDrawer.Trigger>
							)}
							closeOnFocusOutside
						>
							<Tabs position="relative">
								<TabList>
									<Tab>{__("Normal", "magazine-blocks")}</Tab>
									<Tab>{__("Hover", "magazine-blocks")}</Tab>
								</TabList>
								<TabPanels>
									<TabPanel>
										<Reset
											saved={readMoreColor}
											resetKey="readMoreColor"
											onReset={(v) => {
												setAttributes({
													readMoreColor: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={readMoreColor}
											onChange={(v) =>
												setAttributes({
													readMoreColor: v,
												})
											}
										/>
									</TabPanel>
									<TabPanel>
										<Reset
											saved={readMorewHoverColor}
											resetKey="readMorewHoverColor"
											onReset={(v) => {
												setAttributes({
													readMorewHoverColor: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={readMorewHoverColor}
											onChange={(v) =>
												setAttributes({
													readMorewHoverColor: v,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						</PopoverDrawer>
						<PopoverDrawer
							label={__("Background", "magazine-blocks")}
						>
							<Tabs position="relative">
								<TabList>
									<Tab>{__("Normal", "magazine-blocks")}</Tab>
									<Tab>{__("Hover", "magazine-blocks")}</Tab>
								</TabList>
								<TabPanels>
									<TabPanel>
										<Reset
											saved={readMoreBackground}
											resetKey="readMoreBackground"
											onReset={(v) => {
												setAttributes({
													readMoreBackground: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<Background
											value={readMoreBackground}
											onChange={(v) =>
												setAttributes({
													readMoreBackground: v,
												})
											}
										/>
									</TabPanel>
									<TabPanel>
										<Reset
											saved={readMoreHoverBackground}
											resetKey="readMoreHoverBackground"
											onReset={(v) => {
												setAttributes({
													readMoreHoverBackground: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<Background
											value={readMoreHoverBackground}
											onChange={(v) =>
												setAttributes({
													readMoreHoverBackground: v,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						</PopoverDrawer>
						<Slider
							onChange={(val: any) =>
								setAttributes({ readMoreSpacing: val })
							}
							label={__("Spacing", "magazine-blocks")}
							units={["px", "em", "%"]}
							responsive={true}
							min={0}
							max={100}
							value={readMoreSpacing}
							resetAttributeKey="readMoreSpacing"
						/>
						<Dimensions
							value={readMorePadding}
							responsive
							label={__("Padding", "magazine-blocks")}
							type="margin"
							defaultUnit="px"
							units={["px", "rem", "em", "%"]}
							onChange={(val) =>
								setAttributes({ readMorePadding: val })
							}
							resetAttributeKey="readMorePadding"
						/>
						<Toggle
							checked={enableReadMoreBorder}
							onChange={() =>
								setAttributes({
									enableReadMoreBorder: !enableReadMoreBorder,
								})
							}
							label={__("Border", "magazine-blocks")}
						/>
						{enableReadMoreBorder && (
							<Tabs isFitted variant="unstyled">
								<TabList
									border="1px solid #E0E0E0"
									p="4px"
									borderRadius="2px"
								>
									<Tab
										_selected={{
											color: "white",
											bg: "#7E36F4",
											borderRadius: "2px",
										}}
									>
										Normal
									</Tab>
									<Tab
										_selected={{
											color: "white",
											bg: "#7E36F4",
											borderRadius: "2px",
										}}
									>
										Hover
									</Tab>
								</TabList>
								<TabPanels>
									<TabPanel p="16px 0">
										<Border
											value={readMoreBorder}
											onChange={(v) => {
												setAttributes({
													readMoreBorder: v,
												});
											}}
											resetAttributeKey="readMoreBorder"
										/>
										<BoxShadow
											resetAttributeKey="readMoreBoxShadow"
											value={readMoreBoxShadow}
											onChange={(val) =>
												setAttributes({
													readMoreBoxShadow: val,
												})
											}
										/>
									</TabPanel>
									<TabPanel p="16px 0">
										<Border
											value={readMoreHoverBorder}
											onChange={(v) => {
												setAttributes({
													readMoreHoverBorder: v,
												});
											}}
											resetAttributeKey="readMoreHoverBorder"
										/>
										<BoxShadow
											resetAttributeKey="readMoreBoxShadowHover"
											value={readMoreBoxShadowHover}
											onChange={(val) =>
												setAttributes({
													readMoreBoxShadowHover: val,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						)}
					</Panel>
				)}

				{enableViewMore && (
					<Panel title={__("View All", "magazine-blocks")}>
						<PopoverDrawer
							label={__("Text Color", "magazine-blocks")}
							trigger={(props) => (
								<PopoverDrawer.Trigger {...props}>
									<ZStack
										items={[
											{
												color: viewMoreColor,
												id: "viewMoreColor",
											},
											{
												color: viewMoreHoverColor,
												id: "viewMoreHoverColor",
											},
										]}
									/>
								</PopoverDrawer.Trigger>
							)}
							closeOnFocusOutside
						>
							<Tabs position="relative">
								<TabList>
									<Tab>{__("Normal", "magazine-blocks")}</Tab>
									<Tab>{__("Hover", "magazine-blocks")}</Tab>
								</TabList>
								<TabPanels>
									<TabPanel>
										<Reset
											saved={viewMoreColor}
											resetKey="viewMoreColor"
											onReset={(v) => {
												setAttributes({
													viewMoreColor: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={viewMoreColor}
											onChange={(v) =>
												setAttributes({
													viewMoreColor: v,
												})
											}
										/>
									</TabPanel>
									<TabPanel>
										<Reset
											saved={viewMoreHoverColor}
											resetKey="viewMoreHoverColor"
											onReset={(v) => {
												setAttributes({
													viewMoreHoverColor: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={viewMoreHoverColor}
											onChange={(v) =>
												setAttributes({
													viewMoreHoverColor: v,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						</PopoverDrawer>
						<PopoverDrawer
							label={__("Background", "magazine-blocks")}
						>
							<Tabs position="relative">
								<TabList>
									<Tab>{__("Normal", "magazine-blocks")}</Tab>
									<Tab>{__("Hover", "magazine-blocks")}</Tab>
								</TabList>
								<TabPanels>
									<TabPanel>
										<Reset
											saved={viewMoreBackground}
											resetKey="viewMoreBackground"
											onReset={(v) => {
												setAttributes({
													viewMoreBackground: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<Background
											value={viewMoreBackground}
											onChange={(v) =>
												setAttributes({
													viewMoreBackground: v,
												})
											}
										/>
									</TabPanel>
									<TabPanel>
										<Reset
											saved={viewMoreHoverBackground}
											resetKey="viewMoreHoverBackground"
											onReset={(v) => {
												setAttributes({
													viewMoreHoverBackground: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<Background
											value={viewMoreHoverBackground}
											onChange={(v) =>
												setAttributes({
													viewMoreHoverBackground: v,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						</PopoverDrawer>
						<Slider
							onChange={(val: any) =>
								setAttributes({ viewMoreSpacing: val })
							}
							label={__("Spacing", "magazine-blocks")}
							units={["px", "em", "%"]}
							responsive={true}
							min={0}
							max={100}
							value={viewMoreSpacing}
							resetAttributeKey="viewMoreSpacing"
						/>
						<Dimensions
							value={viewMorePadding}
							responsive
							label={__("Padding", "magazine-blocks")}
							type="margin"
							defaultUnit="px"
							units={["px", "rem", "em", "%"]}
							onChange={(val) =>
								setAttributes({ viewMorePadding: val })
							}
							resetAttributeKey="viewMorePadding"
						/>
						<Toggle
							checked={enableViewMoreBorder}
							onChange={() =>
								setAttributes({
									enableViewMoreBorder: !enableViewMoreBorder,
								})
							}
							label={__("Border", "magazine-blocks")}
						/>
						{enableViewMoreBorder && (
							<Tabs isFitted variant="unstyled">
								<TabList
									border="1px solid #E0E0E0"
									p="4px"
									borderRadius="2px"
								>
									<Tab
										_selected={{
											color: "white",
											bg: "#7E36F4",
											borderRadius: "2px",
										}}
									>
										Normal
									</Tab>
									<Tab
										_selected={{
											color: "white",
											bg: "#7E36F4",
											borderRadius: "2px",
										}}
									>
										Hover
									</Tab>
								</TabList>
								<TabPanels>
									<TabPanel p="16px 0">
										<Border
											value={viewMoreBorder}
											onChange={(v) => {
												setAttributes({
													viewMoreBorder: v,
												});
											}}
											resetAttributeKey="viewMoreBorder"
										/>
										<BoxShadow
											resetAttributeKey="viewMoreBoxShadow"
											value={viewMoreBoxShadow}
											onChange={(val) =>
												setAttributes({
													viewMoreBoxShadow: val,
												})
											}
										/>
									</TabPanel>
									<TabPanel p="16px 0">
										<Border
											value={viewMoreHoverBorder}
											onChange={(v) => {
												setAttributes({
													viewMoreHoverBorder: v,
												});
											}}
											resetAttributeKey="viewMoreHoverBorder"
										/>
										<BoxShadow
											resetAttributeKey="viewMoreBoxShadowHover"
											value={viewMoreBoxShadowHover}
											onChange={(val) =>
												setAttributes({
													viewMoreBoxShadowHover: val,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						)}
						<Slider
							label={__("Icon Size", "magazine-blocks")}
							min={0}
							max={50}
							value={iconSize}
							onChange={(val: any) =>
								setAttributes({ iconSize: val })
							}
							responsive
							units={["px", "em", "%"]}
							resetAttributeKey="iconSize"
						/>
						<Typography
							value={viewAllTypography}
							onChange={(val) =>
								setAttributes({
									viewAllTypography: val,
								})
							}
							resetAttributeKey="viewAllTypography"
						/>
					</Panel>
				)}

				{enablePagination && (
					<Panel title={__("Pagination", "magazine-blocks")}>
						<PopoverDrawer
							label={__("Text Color", "magazine-blocks")}
							trigger={(props) => (
								<PopoverDrawer.Trigger {...props}>
									<ZStack
										items={[
											{
												color: paginationColor,
												id: "arrowColor",
											},
											{
												color: paginationHoverColor,
												id: "arrowHoverColor",
											},
										]}
									/>
								</PopoverDrawer.Trigger>
							)}
							closeOnFocusOutside
						>
							<Tabs position="relative">
								<TabList>
									<Tab>{__("Normal", "magazine-blocks")}</Tab>
									<Tab>{__("Hover", "magazine-blocks")}</Tab>
								</TabList>
								<TabPanels>
									<TabPanel>
										<Reset
											saved={paginationColor}
											resetKey="paginationColor"
											onReset={(v) => {
												setAttributes({
													paginationColor: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={paginationColor}
											onChange={(v) =>
												setAttributes({
													paginationColor: v,
												})
											}
										/>
									</TabPanel>
									<TabPanel>
										<Reset
											saved={paginationHoverColor}
											resetKey="paginationHoverColor"
											onReset={(v) => {
												setAttributes({
													paginationHoverColor: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={paginationHoverColor}
											onChange={(v) =>
												setAttributes({
													paginationHoverColor: v,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						</PopoverDrawer>
						<PopoverDrawer
							label={__("Background", "magazine-blocks")}
						>
							<Tabs position="relative">
								<TabList>
									<Tab>{__("Normal", "magazine-blocks")}</Tab>
									<Tab>{__("Hover", "magazine-blocks")}</Tab>
								</TabList>
								<TabPanels>
									<TabPanel>
										<Reset
											saved={paginationBackground}
											resetKey="paginationBackground"
											onReset={(v) => {
												setAttributes({
													paginationBackground: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<Background
											value={paginationBackground}
											onChange={(v) =>
												setAttributes({
													paginationBackground: v,
												})
											}
										/>
									</TabPanel>
									<TabPanel>
										<Reset
											saved={paginationHoverBackground}
											resetKey="paginationHoverBackground"
											onReset={(v) => {
												setAttributes({
													paginationHoverBackground:
														v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<Background
											value={paginationHoverBackground}
											onChange={(v) =>
												setAttributes({
													paginationHoverBackground:
														v,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						</PopoverDrawer>
						<Dimensions
							value={paginationPadding}
							responsive
							label={__("Padding", "magazine-blocks")}
							type="padding"
							defaultUnit="px"
							units={["px", "rem", "em", "%"]}
							onChange={(val) =>
								setAttributes({ paginationPadding: val })
							}
							resetAttributeKey="paginationPadding"
						/>
						<Toggle
							checked={!!paginationBorder?.border}
							onChange={() =>
								setAttributes({
									paginationBorder: {
										...paginationBorder,
										border: paginationBorder?.border
											? 0
											: 1,
									},
								})
							}
							label={__("Enable Border", "magazine-blocks")}
						/>
						{!!paginationBorder?.border && (
							<PopoverDrawer
								label={__("Border", "magazine-blocks")}
								closeOnFocusOutside
							>
								<Tabs isFitted variant="unstyled">
									<TabList
										border="1px solid #E0E0E0"
										p="4px"
										borderRadius="2px"
									>
										<Tab
											_selected={{
												color: "white",
												bg: "#7E36F4",
												borderRadius: "2px",
											}}
										>
											Normal
										</Tab>
										<Tab
											_selected={{
												color: "white",
												bg: "#7E36F4",
												borderRadius: "2px",
											}}
										>
											Hover
										</Tab>
									</TabList>
									<TabPanels>
										<TabPanel p="16px 0">
											<Border
												value={paginationBorder}
												onChange={(v) => {
													setAttributes({
														paginationBorder: v,
													});
												}}
												resetAttributeKey="paginationBorder"
											/>
											<BoxShadow
												resetAttributeKey="paginationBoxShadow"
												value={paginationBoxShadow}
												onChange={(val) =>
													setAttributes({
														paginationBoxShadow:
															val,
													})
												}
											/>
										</TabPanel>
										<TabPanel p="16px 0">
											<Border
												value={paginationHoverBorder}
												onChange={(v) => {
													setAttributes({
														paginationHoverBorder:
															v,
													});
												}}
												resetAttributeKey="paginationHoverBorder"
											/>
											<BoxShadow
												resetAttributeKey="paginationBoxShadowHover"
												value={paginationBoxShadowHover}
												onChange={(val) =>
													setAttributes({
														paginationBoxShadowHover:
															val,
													})
												}
											/>
										</TabPanel>
									</TabPanels>
								</Tabs>
							</PopoverDrawer>
						)}
					</Panel>
				)}

				<Panel title={__("Border", "magazine-blocks")}>
					<Tabs isFitted variant="unstyled">
						<TabList
							border="1px solid #E0E0E0"
							p="4px"
							borderRadius="2px"
						>
							<Tab
								_selected={{
									color: "white",
									bg: "#7E36F4",
									borderRadius: "2px",
								}}
							>
								Normal
							</Tab>
							<Tab
								_selected={{
									color: "white",
									bg: "#7E36F4",
									borderRadius: "2px",
								}}
							>
								Hover
							</Tab>
						</TabList>
						<TabPanels>
							<TabPanel p="16px 0">
								<Border
									value={border}
									onChange={(v) => {
										setAttributes({
											border: v,
										});
									}}
									resetAttributeKey="border"
								/>
							</TabPanel>
							<TabPanel p="16px 0">
								<Border
									value={borderHover}
									onChange={(v) => {
										setAttributes({
											borderHover: v,
										});
									}}
									resetAttributeKey="borderHover"
								/>
							</TabPanel>
						</TabPanels>
					</Tabs>
				</Panel>
				{enableNumberList && (
					<Panel title={__("Number List", "magazine-blocks")}>
						<Typography
							value={numberListTypography}
							onChange={(val) =>
								setAttributes({ numberListTypography: val })
							}
							resetAttributeKey="numberListTypography"
						/>
						<PopoverDrawer
							label={__("Number Color", "magazine-blocks")}
							trigger={(props) => (
								<PopoverDrawer.Trigger {...props}>
									<ZStack
										items={[
											{
												color: numberColor,
												id: "numberColor",
											},
										]}
									/>
								</PopoverDrawer.Trigger>
							)}
							closeOnFocusOutside
						>
							<Tabs position="relative">
								<TabList>
									<Tab>{__("Normal", "magazine-blocks")}</Tab>
								</TabList>
								<TabPanels>
									<TabPanel>
										<Reset
											saved={numberColor}
											resetKey="numberColor"
											onReset={(v) => {
												setAttributes({
													numberColor: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={numberColor}
											onChange={(v) =>
												setAttributes({
													numberColor: v,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						</PopoverDrawer>
						<Slider
							label={__("Left Spacing", "magazine-blocks")}
							step={1}
							min={-100}
							max={100}
							onChange={(val: any) =>
								setAttributes({ numberLeftGap: val })
							}
							units={["px", "em", "%"]}
							value={numberLeftGap}
							resetAttributeKey="numberLeftGap"
						/>
						<Slider
							label={__("Top Spacing", "magazine-blocks")}
							step={1}
							min={-100}
							max={500}
							onChange={(val: any) =>
								setAttributes({ numberTopGap: val })
							}
							units={["px", "em", "%"]}
							value={numberTopGap}
							resetAttributeKey="numberTopGap"
						/>
						<PopoverDrawer
							label={__("Background", "magazine-blocks")}
						>
							<Tabs position="relative">
								<TabList>
									<Tab>{__("Normal", "magazine-blocks")}</Tab>
								</TabList>
								<TabPanels>
									<TabPanel>
										<Reset
											saved={numberBackground}
											resetKey="numberBackground"
											onReset={(v) => {
												setAttributes({
													numberBackground: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<Background
											value={numberBackground}
											onChange={(v) =>
												setAttributes({
													numberBackground: v,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						</PopoverDrawer>
						<PopoverDrawer
							label={__("Border", "magazine-blocks")}
							closeOnFocusOutside
						>
							<Tabs isFitted variant="unstyled">
								<TabList
									border="1px solid #E0E0E0"
									p="4px"
									borderRadius="2px"
								>
									<Tab
										_selected={{
											color: "white",
											bg: "#7E36F4",
											borderRadius: "2px",
										}}
									>
										Normal
									</Tab>
								</TabList>
								<TabPanels>
									<TabPanel p="16px 0">
										<Border
											value={numberBorder}
											onChange={(v) => {
												setAttributes({
													numberBorder: v,
												});
											}}
											resetAttributeKey="numberBorder"
										/>
										<BoxShadow
											resetAttributeKey="numberBoxShadow"
											value={numberBoxShadow}
											onChange={(val) =>
												setAttributes({
													numberBoxShadow: val,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						</PopoverDrawer>
						<Slider
							label={__("Width", "magazine-blocks")}
							step={1}
							min={1}
							max={100}
							onChange={(val: any) =>
								setAttributes({ numberWidth: val })
							}
							units={["px", "em", "%"]}
							value={numberWidth}
							resetAttributeKey="numberWidth"
						/>
						<Slider
							label={__("Height", "magazine-blocks")}
							step={1}
							min={1}
							max={100}
							onChange={(val: any) =>
								setAttributes({ numberHeight: val })
							}
							units={["px", "em", "%"]}
							value={numberHeight}
							resetAttributeKey="numberHeight"
						/>
					</Panel>
				)}
			</StyleInspectorControls>
			<AdvancedInspectorControls>
				<AdvancedControls
					blockMargin={blockMargin}
					setAttributes={setAttributes}
					blockPadding={blockPadding}
				/>
				<ResponsiveControls
					{...{
						hideOnDesktop,
						hideOnTablet,
						hideOnMobile,
						setAttributes,
					}}
				/>
			</AdvancedInspectorControls>
		</>
	);
};

export default InspectorControls;
